import { useState } from 'react';
import { Percent, Save, Loader2 } from 'lucide-react';
import { DEFAULT_PLATFORM_PERCENT, PLATFORM_FEE_FLOOR_BPS } from '@evi/shared';
import { updateLocation, getDisplayErrorMessage } from '@/lib/api';
import sharedStyles from './ResidentChargersCard.module.css';
import styles from './LocationPlatformShareCard.module.css';

/** The lowest share the field accepts, in percent: the same floor every
 *  new session's pin is raised to (PLATFORM_FEE_FLOOR_BPS). The PUT
 *  schema's `minimum` is this number. */
const FLOOR_PERCENT = PLATFORM_FEE_FLOOR_BPS / 100;
/** The PUT schema's `maximum`. */
const MAX_PERCENT = 50;

/**
 * EVI platform share (%) — Location.revenue_split, EVI's cut of
 * session revenue at this Location. Tier 2 (Warren, 2026-09-26):
 * "revenue_split: the write path, the admin field, ...".
 *
 * super_admin only. The parent mounts the card only for
 * `authUser.role === 'super_admin'`, the same signal the API checks:
 * PUT /locations/:id answers 403 to any other caller that sends the
 * key. A Location's share is EVI's commercial term, not the operator's.
 *
 * Save model
 * ──────────
 * Explicit Save, as LocationGooglePlaceIdCard: the button enables only
 * when the draft differs from the stored value and is in range
 * (FLOOR_PERCENT to MAX_PERCENT). There is no "clear": the API takes a
 * number, and an absent share already reads as the default.
 *
 * Echo check
 * ──────────
 * An API that predates the field strips an unlisted key (AJV
 * removeAdditional on an additionalProperties:false body) and still
 * answers 200 with the unchanged Location. So a 200 is not proof: the
 * card compares the echoed revenue_split with what it sent and shows
 * an error when they differ, then adopts the echoed value as the truth.
 *
 * Below the floor
 * ───────────────
 * A stored share under 15% predates Tier 2. It is FLAGGED here and
 * never auto-fixed — Warren: "leave them, flag them in admin, do not
 * sweep". New sessions pin at 15% regardless (pinAdminFeeBps); sessions
 * already running settle at the rate they started under.
 */
interface LocationPlatformShareCardProps {
  locationId: string;
  /** Stored Location.revenue_split. null/undefined = never set. */
  value: number | null | undefined;
  /** Fired with the value the server echoed after a save, so the
   *  cached LocationDetail matches what is actually stored. */
  onChange?: (next: number | null) => void;
}

/** A finite number from the input string, or null when blank/garbage. */
function parsePercent(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function LocationPlatformShareCard({
  locationId, value, onChange,
}: LocationPlatformShareCardProps) {
  const stored = typeof value === 'number' ? value : null;
  const initial = stored === null ? '' : String(stored);
  // `null` = not edited yet, so the input tracks the stored value
  // (including after a parent refetch); the first keystroke takes over.
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const effective = draft ?? initial;
  const parsed = parsePercent(effective);
  const inRange = parsed !== null && parsed >= FLOOR_PERCENT && parsed <= MAX_PERCENT;
  const dirty = parsed !== stored;
  // Only flag a typed value; a stored sub-floor share is flagged below.
  const showRangeError = draft !== null && effective.trim() !== '' && !inRange;

  const handleSave = async (): Promise<void> => {
    if (saving || !dirty || !inRange || parsed === null) return;
    const next = parsed;
    setSaving(true);
    setError('');
    setSavedAt(null);
    try {
      const saved = await updateLocation(locationId, { revenue_split: next });
      const echoed = typeof saved.revenue_split === 'number' ? saved.revenue_split : null;
      if (echoed !== next) {
        setError(
          `The server did not store ${next}%: it returned `
          + `${echoed === null ? 'no value' : `${echoed}%`}. The API may predate `
          + 'this field.',
        );
      } else {
        setSavedAt(Date.now());
        setDraft(null);
      }
      onChange?.(echoed);
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Failed to save the platform share'));
      console.error('LocationPlatformShareCard update failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={sharedStyles.card}>
      <div className={sharedStyles.cardTitleRow}>
        <h2 className={sharedStyles.cardTitle}>
          <Percent size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
          EVI platform share (%)
        </h2>
      </div>

      {error && <div className={sharedStyles.alertErr}>{error}</div>}
      {savedAt && !error && (
        <div className={sharedStyles.alertOk}>Saved.</div>
      )}

      {stored === null ? (
        <p className={styles.status}>
          Not set — {DEFAULT_PLATFORM_PERCENT}% default, or the tenant&rsquo;s
          platform fee if one is set.
        </p>
      ) : stored < FLOOR_PERCENT ? (
        <div className={styles.alertWarn}>
          Below the platform floor — new sessions settle at {FLOOR_PERCENT}%.
          Stored: {stored}%, left as is.
        </div>
      ) : (
        <p className={styles.status}>New sessions settle at {stored}%.</p>
      )}

      <div className={styles.fieldRow}>
        <span className={styles.inputWrap}>
          <input
            type="number"
            inputMode="decimal"
            className={`${styles.input} ${showRangeError ? styles.inputInvalid : ''}`}
            min={FLOOR_PERCENT}
            max={MAX_PERCENT}
            step={0.1}
            placeholder={String(DEFAULT_PLATFORM_PERCENT)}
            value={effective}
            disabled={saving}
            aria-label="EVI platform share, percent"
            aria-invalid={showRangeError}
            onChange={(e) => {
              setDraft(e.target.value);
              // Editing after a save clears the inline confirmation so
              // the next save's "Saved." reads as fresh.
              if (savedAt !== null) setSavedAt(null);
              if (error !== '') setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); void handleSave(); }
            }}
          />
          <span className={styles.suffix}>%</span>
        </span>
        <button
          type="button"
          className={styles.saveBtn}
          disabled={!dirty || !inRange || saving}
          onClick={() => { void handleSave(); }}
        >
          {saving ? (
            <>
              <Loader2 size={13} className={styles.spin} /> Saving…
            </>
          ) : (
            <>
              <Save size={13} /> Save
            </>
          )}
        </button>
      </div>

      <p className={styles.hint}>
        {showRangeError
          ? `Enter ${FLOOR_PERCENT}–${MAX_PERCENT}%.`
          : `${FLOOR_PERCENT}–${MAX_PERCENT}%. EVI's share of session revenue at this Location, read before the tenant's platform fee. Every new session is pinned at no less than ${FLOOR_PERCENT}%; a session already running keeps the rate it started with. EVI staff only.`}
      </p>
    </section>
  );
}
